import type { StringFileResponse } from 'models/FileResponse';
import type { IBlotterQuoteItem } from 'models/IBlotterItem';
import type { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { ActivityFilter, IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { HttpError, IHttpClient } from 'utils/http/httpClient.model';

import type {
  BlotterFilterValues,
  IBlotterFilteringApi,
  IBlotterRequest,
  IFetchColumnDefinitionEpicApi,
  ISearchResponse,
  ServerSideBlotterSearchRequest,
} from './common';

export type {
  BlotterFilterValues,
  IBlotterFilter,
  IBlotterOrder,
  IBooleanBlotterFilter,
  IDateBlotterFilter,
  INumberBlotterFilter,
  ISetBlotterFilter,
  ITextBlotterFilter,
  ServerSideBlotterSearchRequest,
} from './common';

export interface IQuoteBlotterRequest extends IBlotterRequest {
  page: number;
  size: number;
  startDate: string;
  endDate?: string;
  activityScope: ActivityFilter;
  salesDeskId?: number;
  currency?: string;
  product?: string;
  platforms?: string;
  statuses?: string[];
  filters?: any;
}

export interface IQuotesBlotterFetchCsvApi {
  legacyFetchCSV(request: IQuoteBlotterRequest): Observable<StringFileResponse>;
  fetchCSV(request: ServerSideBlotterSearchRequest): Observable<StringFileResponse>;
}

export interface IQuoteFilteringApi extends IBlotterFilteringApi {}

export interface IFetchItemsEpicApi {
  serverSideSearch(request: ServerSideBlotterSearchRequest): Observable<ISearchResponse<IBlotterQuoteItem>>;
}

export interface IQuotesBlotterApi
  extends
    IFetchItemsEpicApi,
    IFetchColumnDefinitionEpicApi<IBlotterQuoteItem>,
    IQuotesBlotterFetchCsvApi,
    IQuoteFilteringApi {}

export function quotesBlotterApiConnector(http: IHttpClient): IQuotesBlotterApi {
  return {
    serverSideSearch(request) {
      return http
        .postJSON<ISearchResponse<IBlotterQuoteItem>>({
          url: 'api/v4/blotter/quotes',
          body: request,
        })
        .pipe(map(({ payload }) => payload));
    },
    fetchFilterValues(field: string) {
      return http
        .getJSON<BlotterFilterValues>({
          url: `api/v4/blotter/quotes/filterValues?field=${field}`,
        })
        .pipe(map(({ payload }) => payload));
    },
    fetchColumnDefinition() {
      return http
        .getJSON<{ data: { columnDefinitions: IBlotterColumnDefinition<IBlotterQuoteItem>[] }; isSuccess: boolean }>({
          url: 'api/v1/blotter/column-definitions/quotes',
        })
        .pipe(map(({ payload }) => payload.data?.columnDefinitions));
    },
    legacyFetchCSV(request: IQuoteBlotterRequest) {
      return http
        .postRAW({
          url: 'api/v1/blotter/quotes/csv',
          body: request,
        })
        .pipe(
          map(response => ({
            data: response.payload,
            filename: response.attachmentName,
          })),
        );
    },
    fetchCSV(request: ServerSideBlotterSearchRequest) {
      return http
        .postRAW({
          url: 'api/v4/blotter/quotes/export',
          headers: { Accept: 'text/csv' },
          body: request,
          parseErrorPayload: true,
          timeOut: 600_000, // OS/Browser system timeout is likely to be shorter
        } satisfies Parameters<IHttpClient['postRAW']>[0])
        .pipe(
          map(response => ({
            data: response.payload,
            filename: response.attachmentName,
          })),
          catchError((err: HttpError) => (err.status === 400 ? throwError(() => err.payload) : throwError(() => err))),
        );
    },
  };
}
